import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/authStore';
import { Lock, Mail, User, Building, Globe, AlertCircle, UserPlus } from 'lucide-react';

const registerSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  companyName: z.string().optional(),
  country: z.string().min(1, 'Country is required'),
  phone: z.string().optional(),
  agreeTerms: z.boolean().refine((val) => val === true, 'You must accept terms & privacy policy'),
});

type RegisterFormData = z.infer<typeof registerSchema>;

export const RegisterPage: React.FC = () => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
  });

  const onSubmit = async (data: RegisterFormData) => {
    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      const res = await api.post('/auth/register', data);
      if (res.data?.success) {
        const { token, user, clientProfile } = res.data.data;
        login(token, user, clientProfile);
        navigate('/client/dashboard');
      }
    } catch (err: any) {
      setErrorMessage(err.response?.data?.error?.message || 'Failed to create client account.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-xl w-full space-y-8 p-8 sm:p-10 rounded-2xl bg-white border border-editorial-border shadow-xs">
        
        <div className="text-center space-y-2">
          <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-brand-500/50 bg-white p-0.5 mx-auto shadow-md flex items-center justify-center">
            <img
              src="/ftt-logo.png"
              alt="For The Truth Logo"
              className="w-full h-full object-contain rounded-full"
            />
          </div>
          <h2 className="font-extrabold text-2xl sm:text-3xl text-slate-900 tracking-tight">
            Create Author &amp; Client Account
          </h2>
          <p className="text-xs text-slate-500">
            Register to track publishing projects, review editorial proofs, and manage documents.
          </p>
        </div>

        {errorMessage && (
          <div className="p-4 rounded-lg bg-rose-50 border border-rose-200 text-rose-800 text-xs flex items-center gap-2">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                First Name *
              </label>
              <input
                type="text"
                {...register('firstName')}
                className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 text-xs focus:ring-2 focus:ring-brand-500 focus:outline-hidden"
                placeholder="Arthur"
              />
              {errors.firstName && <p className="text-rose-600 text-[11px] mt-1">{errors.firstName.message}</p>}
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                Last Name *
              </label>
              <input
                type="text"
                {...register('lastName')}
                className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 text-xs focus:ring-2 focus:ring-brand-500 focus:outline-hidden"
                placeholder="Pendleton"
              />
              {errors.lastName && <p className="text-rose-600 text-[11px] mt-1">{errors.lastName.message}</p>}
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
              Email Address *
            </label>
            <input
              type="email"
              {...register('email')}
              className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 text-xs focus:ring-2 focus:ring-brand-500 focus:outline-hidden"
              placeholder="author@example.com"
            />
            {errors.email && <p className="text-rose-600 text-[11px] mt-1">{errors.email.message}</p>}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                Password (min. 8 chars) *
              </label>
              <input
                type="password"
                {...register('password')}
                className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 text-xs focus:ring-2 focus:ring-brand-500 focus:outline-hidden"
                placeholder="••••••••"
              />
              {errors.password && <p className="text-rose-600 text-[11px] mt-1">{errors.password.message}</p>}
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                Country *
              </label>
              <input
                type="text"
                {...register('country')}
                className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 text-xs focus:ring-2 focus:ring-brand-500 focus:outline-hidden"
                placeholder="e.g. United Kingdom"
              />
              {errors.country && <p className="text-rose-600 text-[11px] mt-1">{errors.country.message}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                Organization / Imprint (Optional)
              </label>
              <input
                type="text"
                {...register('companyName')}
                className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 text-xs focus:ring-2 focus:ring-brand-500 focus:outline-hidden"
                placeholder="e.g. Grace Seminary Publications"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                Phone Number (Optional)
              </label>
              <input
                type="tel"
                {...register('phone')}
                className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 text-xs focus:ring-2 focus:ring-brand-500 focus:outline-hidden"
                placeholder="+1 (555) 000-0000"
              />
            </div>
          </div>

          <div className="pt-2">
            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                id="agreeTerms"
                {...register('agreeTerms')}
                className="w-4 h-4 mt-0.5 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
              />
              <label htmlFor="agreeTerms" className="text-xs text-slate-600">
                I accept the For The Truth Publishing terms of service and author privacy policy.
              </label>
            </div>
            {errors.agreeTerms && <p className="text-rose-600 text-[11px] mt-1">{errors.agreeTerms.message}</p>}
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-3 rounded-lg bg-brand-600 hover:bg-brand-700 text-white font-bold text-xs flex items-center justify-center gap-2 shadow-sm transition-colors disabled:opacity-50 mt-4"
          >
            {isSubmitting ? (
              <span>Creating Client Account...</span>
            ) : (
              <>
                <span>Register Client Account</span>
                <UserPlus className="w-3.5 h-3.5" />
              </>
            )}
          </button>
        </form>

        <div className="text-center text-xs text-slate-500">
          <span>Already have an account? </span>
          <Link to="/login" className="font-bold text-brand-700 hover:underline">
            Sign In
          </Link>
        </div>

      </div>
    </div>
  );
};
